import React, { type ReactNode, Suspense, useCallback, useMemo, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { getATLContextUrl } from '@atlaskit/atlassian-context';
import { GiveKudosLauncherLazy, KudosType } from '@atlaskit/give-kudos';

import type { ActionMenuItem } from './ActionMenu';

const messages = defineMessages({
	giveKudosLabel: {
		id: 'user-profile-card.useKudosMenuItem.giveKudosLabel',
		defaultMessage: 'Give kudos',
		description: 'Label for the action menu entry that opens the Give Kudos drawer',
	},
});

/**
 * Stable analytics source attributed to kudos drawer events fired from this card.
 */
const ANALYTICS_SOURCE = 'user-profile-card';

interface UseKudosMenuItemArgs {
	accountId: string;
	cloudId: string;
	viewerId?: string;
}

interface UseKudosMenuItemResult {
	menuItem: ActionMenuItem | null;
	launcher: ReactNode | null;
}

/**
 * Encapsulates the Give-Kudos integration for the action menu.
 */
export function useKudosMenuItem({
	accountId,
	cloudId,
	viewerId,
}: UseKudosMenuItemArgs): UseKudosMenuItemResult {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	const onClose = useCallback(() => setIsOpen(false), []);
	const onOpen = useCallback(() => setIsOpen(true), []);

	const recipient = useMemo(
		() => ({ type: KudosType.INDIVIDUAL as const, recipientId: accountId }),
		[accountId],
	);

	const label = formatMessage(messages.giveKudosLabel);
	const menuItem = useMemo<ActionMenuItem>(
		() => ({ kind: 'button', id: 'give-kudos', label, onClick: onOpen }),
		[label, onOpen],
	);

	const teamCentralBaseUrl = getATLContextUrl('home');
	const suppressed = !teamCentralBaseUrl || (!!viewerId && viewerId === accountId);

	if (suppressed) {
		return { menuItem: null, launcher: null };
	}

	const launcher = (
		<Suspense fallback={null}>
			<GiveKudosLauncherLazy
				isOpen={isOpen}
				onClose={onClose}
				recipient={recipient}
				analyticsSource={ANALYTICS_SOURCE}
				teamCentralBaseUrl={teamCentralBaseUrl}
				cloudId={cloudId}
			/>
		</Suspense>
	);

	return { menuItem, launcher };
}
